/* Copyright (c) 2020-present CloudIO Inc */

import { allSwichColumns } from 'componentregistry/components/DataGrid/columns/BaseColumn';
import { Platform } from '../../../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import { defaultValue } from './SwitchColumnCase';

const SwitchColumnCaseManager: ComponentManagerFn<'SwitchColumnCase'> = (
  platform: Platform,
): ComponentManager<'SwitchColumnCase'> => {
  return {
    ...baseComponentManager<'SwitchColumnCase'>(platform, 'SwitchColumnCase'),
    type: 'SwitchColumnCase',
    name: 'Switch Column Case',
    isChildAllowed: (page, item, childType) =>
      (allSwichColumns as ItemType[]).includes(childType),
    defaultValue,
    props: {
      case: {
        label: 'Case',
        type: 'string',
        helpText:
          'Provide a constant string value (without quotes) for this case to match the above switch expression. An empty case will be matched with any switch expression.',
      },
    },
    getDisplayText: (item) => item.props.case ?? '',
    category: 'Data Grid',
    icon: { iconName: 'ballot-check' },
    description: `**Overview**<br/>
The Switch Column Case acts as a conditional branch within a Switch Column.
It defines a specific "Match Value" and the corresponding Column Component (e.g., a String Column, Number Column, or Button Column) that should be displayed when that match occurs. Think of it as the \`case "Value":\` statement in a standard programming switch block.

**1. Configuration**<br/>
* **Case:** The value to check against the parent Switch Column's expression.
* **Matching Logic:** If the parent expression evaluates to this value (e.g., "TypeA"), the grid renders the column component inside this Case block.
* **Default Case:** If you leave this field Empty, this component acts as the "Default" (Fallback) case. It will render if no other cases match the expression.

**2. Composition (The Content)**<br/>
The Case component is a wrapper. You must define what actually appears when the case is matched.
* **Allowed Children:** You can add one of the standard column types as a child:
    * String Column
    * Number Column
    * Button Column
    * Date Column
    * Select Column
    * (And any other standard grid column type)

**3. Example Workflow**<br/>
* **Parent Switch Column:** Expression = \`$.status\`
* **Case A:** case = "Open" -> Child = Button Column (Label: "Close Ticket")
* **Case B:** case = "Closed" -> Child = String Column (Label: "Archived", Read-Only)`,
  };
};

export default SwitchColumnCaseManager;
